import { useState } from 'react';
import type { CreditPack } from '../lib/api';

/** Bottom sheet: unlock a paid episode with credits. */
export function UnlockSheet({
  title,
  price,
  balance,
  busy,
  onUnlock,
  onGetCredits,
  onClose,
}: {
  title: string;
  price: number;
  balance: number;
  busy: boolean;
  onUnlock: () => void;
  onGetCredits: () => void;
  onClose: () => void;
}) {
  const enough = balance >= price;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Unlock episode">
        <div className="sheet-handle" />
        <div className="kicker">Next episode</div>
        <h3>{title}</h3>
        <p className="muted">
          Unlock for <b>{price} credits</b>. You have <b>{balance}</b>.
        </p>
        {enough ? (
          <button className="cta" disabled={busy} onClick={onUnlock}>
            {busy ? 'Unlocking…' : `Unlock · ${price} credits`}
          </button>
        ) : (
          <button className="cta" onClick={onGetCredits}>
            Get credits to continue
          </button>
        )}
        <button className="cta secondary" onClick={onClose}>
          Not now
        </button>
      </div>
    </div>
  );
}

/** Bottom sheet: credit packs. Tapping one records purchase intent; the server gifts credits in this phase. */
export function BuySheet({ packs, busy, onPick, onClose }: { packs: CreditPack[]; busy: string | null; onPick: (sku: string) => void; onClose: () => void }) {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Get credits">
        <div className="sheet-handle" />
        <div className="kicker">Credits</div>
        <h3>Keep the story going</h3>
        <p className="muted">Credits unlock new episodes. Choose a pack:</p>
        <div className="packs">
          {packs.map((p) => (
            <button
              key={p.sku}
              className={`pack${picked === p.sku ? ' picked' : ''}`}
              disabled={busy !== null}
              onClick={() => {
                setPicked(p.sku);
                onPick(p.sku);
              }}
            >
              <div className="pack-top">
                <span className="pack-credits">{p.label ?? `${p.credits} credits`}</span>
                {p.badge ? <span className="chip">{p.badge}</span> : null}
              </div>
              <div className="pack-price">{busy === p.sku ? '…' : `$${p.priceUsd.toFixed(2)}`}</div>
            </button>
          ))}
        </div>
        <button className="cta secondary" onClick={onClose}>
          Maybe later
        </button>
      </div>
    </div>
  );
}

export function GiftToast({ message, onDone }: { message: string; onDone: () => void }) {
  return (
    <div className="sheet-backdrop" onClick={onDone}>
      <div className="sheet gift" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="gift-emoji">🎁</div>
        <h3>On the house</h3>
        <p className="muted">{message}</p>
        <button className="cta" onClick={onDone}>
          Continue
        </button>
      </div>
    </div>
  );
}
